import React, { useRef, useState, useEffect, memo } from 'react';
import type { ExecutionStep } from '../../../core/types/rust-engine-interfaces';
import type { VisualElement, Container, Node, VisualEdge, Annotation } from '../../../core/types/visualizer.types';

interface GenericElementVisualizerProps {
  currentStep: ExecutionStep | null;
  algorithmId?: string;
}

function getElements(currentStep: ExecutionStep | null): VisualElement[] {
  if (!currentStep) return [];
  const payload = currentStep.payload;

  if (payload.type === 'generic') {
    const data = payload.data as Record<string, unknown> | undefined;
    const elements = data?.elements;
    return Array.isArray(elements) ? (elements as VisualElement[]) : [];
  }

  // Try to recover elements from any payload that carries them
  const data = (payload as { data?: Record<string, unknown> }).data;
  const elements = (payload as { elements?: unknown }).elements ?? data?.elements;
  return Array.isArray(elements) ? (elements as VisualElement[]) : [];
}

/** Detect the lazy 3-node Input→Algo→Output box-diagram stub. */
function isBoxDiagramStub(elements: VisualElement[]): boolean {
  const ids = new Set(elements.map((e) => e.id));
  return ids.has('node-input') && ids.has('node-algo') && ids.has('node-result');
}

function getHighlightIds(currentStep: ExecutionStep | null): Set<string> {
  if (currentStep?.payload.type !== 'generic') {
    return new Set();
  }
  const raw = currentStep.metadata?.highlightIds ?? currentStep.payload.metadata?.highlightIds;
  return new Set(Array.isArray(raw) ? raw : []);
}

type NodeShape = 'circle' | 'rect' | 'diamond' | 'rounded';

function resolveNodeShape(node: Node, isNested: boolean): NodeShape {
  const meta = node.metadata;
  if (meta?.index !== undefined) return 'rect';
  if (meta?.isCurrent && !meta?.isVisited) return 'diamond';
  if (isNested) return 'rounded';
  return 'circle';
}

function renderSemanticNode(
  node: Node,
  index: number,
  highlightIds: Set<string>,
  isNested: boolean,
): React.ReactElement {
  const isHighlighted = node.id && highlightIds.has(node.id);
  const fill = node.style?.fillColor ?? (isNested ? '#0f172a' : '#1e293b');
  const stroke = isHighlighted ? '#f59e0b' : (node.style?.strokeColor ?? '#60a5fa');
  const strokeWidth = isHighlighted ? 3 : (node.style?.strokeWidth ?? 2);
  const opacity = node.style?.opacity ?? 1;
  const shape = resolveNodeShape(node, isNested);
  const r = isNested ? 18 : 24;
  const label = String(node.label ?? node.value ?? '');
  const fontSize = isNested ? 10 : 12;

  const shapeEl = (() => {
    switch (shape) {
      case 'rect': {
        const w = 36, h = 28;
        return (
          <rect
            x={node.position.x - w / 2}
            y={node.position.y - h / 2}
            width={w}
            height={h}
            rx={3}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            opacity={opacity}
            className={isHighlighted ? 'animate-pulse' : ''}
          />
        );
      }
      case 'diamond': {
        const s = r;
        const cx = node.position.x, cy = node.position.y;
        return (
          <polygon
            points={`${cx},${cy - s} ${cx + s},${cy} ${cx},${cy + s} ${cx - s},${cy}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            opacity={opacity}
            className={isHighlighted ? 'animate-pulse' : ''}
          />
        );
      }
      case 'rounded': {
        const w = 40, h = 30;
        return (
          <rect
            x={node.position.x - w / 2}
            y={node.position.y - h / 2}
            width={w}
            height={h}
            rx={10}
            ry={10}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            opacity={opacity}
            className={isHighlighted ? 'animate-pulse' : ''}
          />
        );
      }
      default:
        return (
          <circle
            cx={node.position.x}
            cy={node.position.y}
            r={r}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            opacity={opacity}
            className={isHighlighted ? 'animate-pulse' : ''}
          />
        );
    }
  })();

  return (
    <g key={node.id || `node-${index}`}>
      {shapeEl}
      <text
        x={node.position.x}
        y={node.position.y}
        fill="#e2e8f0"
        fontSize={fontSize}
        fontWeight="700"
        textAnchor="middle"
        dominantBaseline="middle"
        className="select-none"
      >
        {label.slice(0, isNested ? 3 : 6)}
      </text>
      {node.value !== undefined && String(node.value) !== String(node.label) && (
        <text
          x={node.position.x}
          y={node.position.y + r + 12}
          fill="#94a3b8"
          fontSize={11}
          textAnchor="middle"
          className="select-none"
        >
          {String(node.value)}
        </text>
      )}
    </g>
  );
}

interface StableViewBox { x: number; y: number; width: number; height: number }

export const GenericElementVisualizer: React.FC<GenericElementVisualizerProps> = memo(({
  currentStep,
  algorithmId: _algorithmId = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 700, h: 450 });
  const stableVBRef = useRef<StableViewBox | null>(null);
  const prevSizeRef = useRef<{ w: number; h: number }>({ w: 700, h: 450 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(300, Math.round(width)), h: Math.max(200, Math.round(height)) });
    });
    ro.observe(el);
    const rect = el.getBoundingClientRect();
    setSize({ w: Math.max(300, Math.round(rect.width)), h: Math.max(200, Math.round(rect.height)) });
    return () => ro.disconnect();
  }, []);

  const { w: width, h: height } = size;

  // Reset stable viewBox when container size changes so it recomputes
  // from the new dimensions instead of only growing.
  if (prevSizeRef.current.w !== width || prevSizeRef.current.h !== height) {
    stableVBRef.current = null;
    prevSizeRef.current = { w: width, h: height };
  }

  const elements = getElements(currentStep);
  const highlightIds = getHighlightIds(currentStep);
  const nodes = elements.filter((element) => element.type === 'node') as Node[];
  const edges = elements.filter((element) => element.type === 'edge') as VisualEdge[];
  const containers = elements.filter((element) => element.type === 'container') as Container[];
  const annotations = elements.filter((element) => element.type === 'annotation') as Annotation[];

  if (elements.length === 0) {
    return (
      <div ref={containerRef} className="flex h-full w-full items-center justify-center text-slate-400">
        <div className="text-center">
          <p className="text-sm">No drawable frame data available.</p>
          <p className="mt-1 text-xs text-slate-500">{currentStep?.metadata?.explanation ?? 'This step has no visual elements.'}</p>
        </div>
      </div>
    );
  }

  if (isBoxDiagramStub(elements)) {
    return (
      <div ref={containerRef} className="flex h-full w-full items-center justify-center">
        <div className="text-center max-w-sm">
          <p className="text-slate-300 text-sm font-semibold mb-1">Visualization Initializing</p>
          <p className="text-slate-500 text-xs">{currentStep?.metadata?.explanation ?? 'Generating algorithm state frames…'}</p>
        </div>
      </div>
    );
  }

  const nodePositions = new Map(
    nodes.map((node) => [node.id, node.position])
  );

  const highlightedNodes = nodes.filter((node) => node.id && highlightIds.has(node.id));

  const frameViewBox: StableViewBox = (() => {
    const positions = nodes.map((n) => n.position);
    const xs = positions.map((p) => p.x);
    const ys = positions.map((p) => p.y);
    const minX = Math.min(...xs, 0);
    const minY = Math.min(...ys, 0);
    const maxX = Math.max(...xs, width);
    const maxY = Math.max(...ys, height);
    const pad = 20;
    const vbW = Math.max(maxX - minX + pad * 2, 100);
    const vbH = Math.max(maxY - minY + pad * 2, 100);
    return { x: minX - pad, y: minY - pad, width: vbW, height: vbH };
  })();

  // Expand the stable viewBox to encompass the current frame's extents.
  // The box only ever grows — never shrinks — so the scene never pans or
  // zooms as nodes move between steps.
  const prev = stableVBRef.current;
  if (prev === null) {
    stableVBRef.current = frameViewBox;
  } else {
    const newX = Math.min(prev.x, frameViewBox.x);
    const newY = Math.min(prev.y, frameViewBox.y);
    const newRight  = Math.max(prev.x + prev.width,  frameViewBox.x + frameViewBox.width);
    const newBottom = Math.max(prev.y + prev.height, frameViewBox.y + frameViewBox.height);
    stableVBRef.current = { x: newX, y: newY, width: newRight - newX, height: newBottom - newY };
  }
  const stableViewBox = stableVBRef.current;

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      <svg
        width={width}
        height={height}
        viewBox={`${stableViewBox.x} ${stableViewBox.y} ${stableViewBox.width} ${stableViewBox.height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        <defs>
          <filter id="generic-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <marker id="generic-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#64748b" />
          </marker>
          <marker id="generic-arrow-hi" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#f59e0b" />
          </marker>
        </defs>
        {edges.map((edge, index) => {
          const from = nodePositions.get(edge.from);
          const to = nodePositions.get(edge.to);
          if (!from || !to) return null;

          const isHighlighted = edge.id && highlightIds.has(edge.id);
          const stroke = isHighlighted ? '#f59e0b' : (edge.style?.strokeColor ?? '#64748b');
          const strokeWidth = isHighlighted ? 4 : (edge.style?.strokeWidth ?? 2);
          const opacity = isHighlighted ? 1 : (edge.style?.opacity ?? 0.75);

          const mx = (from.x + to.x) / 2;
          const my = (from.y + to.y) / 2;
          const edgeWeight = edge.weight;
          return (
            <g key={edge.id || `edge-${index}`}>
              <line
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={stroke}
                strokeWidth={strokeWidth}
                opacity={opacity}
                markerEnd={isHighlighted ? 'url(#generic-arrow-hi)' : 'url(#generic-arrow)'}
              />
              {edgeWeight !== undefined && (
                <text x={mx} y={my - 6} fill="#94a3b8" fontSize={10} textAnchor="middle" className="select-none">
                  {edgeWeight}
                </text>
              )}
            </g>
          );
        })}

        {/* Render containers first (background) with nested elements */}
        {containers.map((container, index) => {
          const isHighlighted = container.id && highlightIds.has(container.id);
          const fill = container.style?.fillColor ?? 'rgba(30, 41, 59, 0.5)';
          const stroke = isHighlighted ? '#f59e0b' : (container.style?.strokeColor ?? '#475569');
          const strokeWidth = isHighlighted ? 3 : (container.style?.strokeWidth ?? 1.5);
          const opacity = container.style?.opacity ?? 1;
          const label = container.metadata?.label;

          return (
            <g key={container.id || `container-${index}`}>
              <rect
                x={container.bounds.x}
                y={container.bounds.y}
                width={container.bounds.width}
                height={container.bounds.height}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
                opacity={opacity}
                rx={8}
                ry={8}
                className={isHighlighted ? 'animate-pulse' : ''}
              />
              {label && (
                <text
                  x={container.bounds.x + 8}
                  y={container.bounds.y - 8}
                  fill={isHighlighted ? '#f59e0b' : '#64748b'}
                  fontSize={12}
                  fontWeight="600"
                  textAnchor="start"
                  className="select-none"
                >
                  {label}
                </text>
              )}
              {container.elements.map((nestedElement, nestedIndex) => {
                if (nestedElement.type === 'node') {
                  const nestedNode = nestedElement as Node;
                  return renderSemanticNode(nestedNode, nestedIndex, highlightIds, true);
                }
                if (nestedElement.type === 'annotation') {
                  const ann = nestedElement as Annotation;
                  return (
                    <text
                      key={ann.id || `nested-ann-${nestedIndex}`}
                      x={ann.position.x}
                      y={ann.position.y}
                      fill={ann.style?.fillColor ?? '#cbd5e1'}
                      fontSize={ann.style?.fontSize ?? 11}
                      textAnchor="middle"
                      className="select-none"
                    >
                      {ann.text}
                    </text>
                  );
                }
                return null;
              })}
            </g>
          );
        })}

        {/* Render top-level nodes with semantic shapes */}
        {nodes.map((node, index) => renderSemanticNode(node, index, highlightIds, false))}

        {annotations.map((annotation, index) => (
          <text
            key={annotation.id || `annotation-${index}`}
            x={annotation.position.x}
            y={annotation.position.y}
            fill={annotation.style?.fillColor ?? '#cbd5e1'}
            fontSize={annotation.style?.fontSize ?? 13}
            textAnchor="middle"
            className="select-none"
          >
            {annotation.text}
          </text>
        ))}
        {highlightedNodes.length > 0 && (
          <g>
            {highlightedNodes.map((node) => (
              <circle
                key={`central-${node.id}`}
                cx={node.position.x}
                cy={node.position.y}
                r={32}
                fill="none"
                stroke="#f59e0b"
                strokeWidth={3}
                opacity={0.9}
                filter="url(#generic-glow)"
                className="animate-pulse"
              />
            ))}
          </g>
        )}
      </svg>
    </div>
  );
});

export default GenericElementVisualizer;
